/**
 * Query-canvas store for the viewer-data block lab.
 *
 * A DESIGN PROTOTYPE (no backend): the user asks a question about the open
 * collection, the assistant (`/v1/assistant/collection-chat`) answers and
 * names the relevant blocks, and we materialize those into a *temporary
 * canvas* — a freeform board that shows ONLY the relevant blocks. The canvas
 * carries three annotation layers the user builds by hand:
 *
 *   1. captions  — a short note pinned over a block
 *   2. groups    — a labelled lasso around several blocks
 *   3. fragments — the verbatim passage pulled from a block's transcript /
 *                  scraped HTML that's most relevant to the question (the
 *                  extraction itself lives in `block-artifacts.ts`)
 *
 * Everything is mirrored to localStorage keyed by canvas id, so a board
 * survives reloads but never leaves the device. The screen owns which canvas
 * is "active"; this module only persists them and notifies subscribers.
 */
import { useEffect, useState } from "react";
// ---------------------------------------------------------------------------
// localStorage persistence (map of canvasId → canvas)
// ---------------------------------------------------------------------------
const STORE_KEY = "vlab:query-canvases";
/** Hard cap so a runaway prototype can't fill the user's localStorage quota. */
const MAX_CANVASES = 40;
function loadMap() {
    try {
        const raw = localStorage.getItem(STORE_KEY);
        if (!raw)
            return {};
        const parsed = JSON.parse(raw);
        if (typeof parsed !== "object" || parsed === null)
            return {};
        return parsed;
    }
    catch {
        return {};
    }
}
const listeners = new Set();
function subscribe(fn) {
    listeners.add(fn);
    return () => {
        listeners.delete(fn);
    };
}
function notify() {
    // Defer out of the current render/commit: writers may run inside a state
    // updater, and notifying synchronously would setState mid-render.
    const fns = [...listeners];
    queueMicrotask(() => {
        for (const fn of fns)
            fn();
    });
}
function saveMap(map) {
    try {
        localStorage.setItem(STORE_KEY, JSON.stringify(map));
    }
    catch {
        // Quota exceeded / storage disabled — prototype, so silently drop.
    }
    notify();
}
function newId(prefix) {
    try {
        return `${prefix}-${crypto.randomUUID()}`;
    }
    catch {
        return `${prefix}-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;
    }
}
/** Deterministic hue (0–359) from a string, for group tints. */
function hueFromString(s) {
    let h = 0;
    for (let i = 0; i < s.length; i++)
        h = (h * 31 + s.charCodeAt(i)) | 0;
    return Math.abs(h) % 360;
}
// ---------------------------------------------------------------------------
// Initial layout — cluster grouped blocks into vertical bands
// ---------------------------------------------------------------------------
//
// These mirror the overlay's coordinate constants (CARD/COL_PITCH/ROW_PITCH/
// ORIGIN) so seeded positions line up with where the overlay renders nodes,
// fragment cards, and group boxes. Each seeded group becomes its own band (a
// 3-column sub-grid); ungrouped blocks fall into a final band. Seeding fixed
// positions up front means creating a manual group later never reflows the
// board.
const LAYOUT_COL_PITCH = 540;
const LAYOUT_ROW_PITCH = 320;
const LAYOUT_ORIGIN = 48;
const LAYOUT_COLS = 3;
const LAYOUT_BAND_GAP = 140;
function layoutPositions(blockIds, groups) {
    const positions = {};
    const present = new Set(blockIds);
    const placed = new Set();
    let y = LAYOUT_ORIGIN;
    const placeBand = (ids) => {
        const list = ids.filter((id) => present.has(id) && !placed.has(id));
        list.forEach((id, i) => {
            const col = i % LAYOUT_COLS;
            const row = Math.floor(i / LAYOUT_COLS);
            positions[id] = {
                x: LAYOUT_ORIGIN + col * LAYOUT_COL_PITCH,
                y: y + row * LAYOUT_ROW_PITCH,
            };
            placed.add(id);
        });
        if (list.length > 0) {
            const rows = Math.ceil(list.length / LAYOUT_COLS);
            y += rows * LAYOUT_ROW_PITCH + LAYOUT_BAND_GAP;
        }
    };
    for (const g of groups)
        placeBand(g.blockIds);
    placeBand(blockIds);
    return positions;
}
// ---------------------------------------------------------------------------
// Mutations (each persists + notifies)
// ---------------------------------------------------------------------------
/** Create + persist a new canvas, returning its full record (id assigned here). */
export function createCanvas(input) {
    const { seedCaptions, seedGroups, ...base } = input;
    const captions = (seedCaptions ?? [])
        .filter((c) => c.text.trim().length > 0)
        .map((c) => ({ id: newId("cap"), blockId: c.blockId, text: c.text.trim() }));
    const groups = (seedGroups ?? [])
        .filter((g) => g.blockIds.length > 0)
        .map((g) => ({
        id: newId("grp"),
        label: g.label,
        blockIds: [...g.blockIds],
        hue: hueFromString(g.label),
    }));
    const canvas = {
        ...base,
        id: newId("qc"),
        createdAt: Date.now(),
        positions: layoutPositions(base.blockIds, groups),
        captions,
        groups,
        fragments: {},
    };
    const map = loadMap();
    map[canvas.id] = canvas;
    // Evict oldest beyond the cap so the store stays bounded.
    const ids = Object.values(map)
        .sort((a, b) => b.createdAt - a.createdAt)
        .map((c) => c.id);
    for (const id of ids.slice(MAX_CANVASES))
        delete map[id];
    saveMap(map);
    return canvas;
}
/** Apply an immutable update to one canvas (no-op if it's gone). */
function update(canvasId, fn) {
    const map = loadMap();
    const current = map[canvasId];
    if (!current)
        return;
    map[canvasId] = fn(current);
    saveMap(map);
}
export function deleteCanvas(canvasId) {
    const map = loadMap();
    if (!map[canvasId])
        return;
    delete map[canvasId];
    saveMap(map);
}
export function setBlockPosition(canvasId, blockId, pos) {
    update(canvasId, (c) => ({ ...c, positions: { ...c.positions, [blockId]: pos } }));
}
/** Drop a block from the board entirely (and any captions/group refs to it). */
export function removeBlock(canvasId, blockId) {
    update(canvasId, (c) => {
        const positions = { ...c.positions };
        delete positions[blockId];
        const fragments = { ...c.fragments };
        delete fragments[blockId];
        return {
            ...c,
            blockIds: c.blockIds.filter((id) => id !== blockId),
            positions,
            fragments,
            captions: c.captions.filter((cap) => cap.blockId !== blockId),
            groups: c.groups.map((g) => ({ ...g, blockIds: g.blockIds.filter((id) => id !== blockId) })),
        };
    });
}
// --- Captions ---------------------------------------------------------------
export function addCaption(canvasId, blockId, text) {
    const caption = { id: newId("cap"), blockId, text };
    update(canvasId, (c) => ({ ...c, captions: [...c.captions, caption] }));
}
export function updateCaption(canvasId, captionId, text) {
    update(canvasId, (c) => ({
        ...c,
        captions: c.captions.map((cap) => (cap.id === captionId ? { ...cap, text } : cap)),
    }));
}
export function removeCaption(canvasId, captionId) {
    update(canvasId, (c) => ({ ...c, captions: c.captions.filter((cap) => cap.id !== captionId) }));
}
// --- Groups -----------------------------------------------------------------
export function addGroup(canvasId, blockIds, label, hue) {
    if (blockIds.length === 0)
        return;
    const group = { id: newId("grp"), label, blockIds: [...blockIds], hue };
    update(canvasId, (c) => ({ ...c, groups: [...c.groups, group] }));
}
export function updateGroupLabel(canvasId, groupId, label) {
    update(canvasId, (c) => ({
        ...c,
        groups: c.groups.map((g) => (g.id === groupId ? { ...g, label } : g)),
    }));
}
export function removeGroup(canvasId, groupId) {
    update(canvasId, (c) => ({ ...c, groups: c.groups.filter((g) => g.id !== groupId) }));
}
// --- Fragments --------------------------------------------------------------
export function setFragment(canvasId, blockId, fragment) {
    update(canvasId, (c) => ({ ...c, fragments: { ...c.fragments, [blockId]: fragment } }));
}
export function clearFragment(canvasId, blockId) {
    update(canvasId, (c) => {
        const fragments = { ...c.fragments };
        delete fragments[blockId];
        return { ...c, fragments };
    });
}
// ---------------------------------------------------------------------------
// Reactive hooks
// ---------------------------------------------------------------------------
/** Reactive view of a single canvas (undefined once deleted / unknown id). */
export function useQueryCanvas(canvasId) {
    const [canvas, setCanvas] = useState(() => canvasId ? loadMap()[canvasId] : undefined);
    useEffect(() => {
        const read = () => setCanvas(canvasId ? loadMap()[canvasId] : undefined);
        read();
        return subscribe(read);
    }, [canvasId]);
    return canvas;
}
/** Reactive list of all saved canvases, newest first. */
export function useQueryCanvases() {
    const [list, setList] = useState(() => Object.values(loadMap()).sort((a, b) => b.createdAt - a.createdAt));
    useEffect(() => {
        const read = () => setList(Object.values(loadMap()).sort((a, b) => b.createdAt - a.createdAt));
        read();
        return subscribe(read);
    }, []);
    return list;
}
// ---------------------------------------------------------------------------
// Pure helpers shared with the canvas UI
// ---------------------------------------------------------------------------
/** "1:05" / "12:03" from a second offset (transcript timestamps). */
export function formatTimestamp(seconds) {
    if (seconds === undefined || Number.isNaN(seconds))
        return undefined;
    const total = Math.max(0, Math.floor(seconds));
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
}
